import { AttentionRefDto } from '@/features/dashboard/attention/types/AttentionRefDto.ts';
import { MessageTemplateType } from '@/features/dashboard/messageTemplates/types/MessageTemplateDto.ts';
import { getMessageTemplateTypeLabel } from '@/features/dashboard/messageTemplates/types/MessageTemplateType.utils.ts';
import { formatDayMonthYear } from '@/utils/dateFormatters.ts';
import { fullNameOf } from '@/utils/personName.ts';
import { reportStatsContractBreach } from '@/types/stats/statsContract.ts';
import { BookingStatus } from '@/types/BookingStatus.ts';

/** Ile podmiotów zmieści się w zdaniu, zanim zacznie się je liczyć. */
const MAX_REFS = 3;

const breach = (message: string): string => {
  reportStatsContractBreach(message);
  return '';
};

/** Etykieta odnośnika wg rodzaju — jedyne miejsce, które wie, po czym człowiek rozpoznaje podmiot. */
export const attentionRefLabel = (ref: AttentionRefDto): string => {
  const kind: unknown = ref.kind;
  if (typeof kind !== 'string') {
    return breach(`Odnośnik bez rodzaju: ${JSON.stringify(ref)}.`);
  }
  // Jeden odnośnik bez rekordu nie może wywrócić całego panelu przez TypeError.
  const field = kind.toLowerCase();
  if ((ref as unknown as Record<string, unknown>)[field] == null) {
    return breach(`Odnośnik "${kind}" nie ma pola "${field}".`);
  }

  switch (ref.kind) {
    case 'BOOKING':
      return ref.booking.bookingCode;
    case 'PARTICIPANT':
      return fullNameOf(ref.participant.firstName, ref.participant.lastName);
    case 'CLIENT':
      return fullNameOf(ref.client.firstName, ref.client.lastName);
    case 'TRANSACTION':
      return (
        ref.transaction.description ||
        formatDayMonthYear(ref.transaction.transactionDate)
      );
    case 'MESSAGE':
      return getMessageTemplateTypeLabel(
        ref.message.messageTemplateType as MessageTemplateType,
      );
    case 'EXPENSE':
      return ref.expense.description || formatDayMonthYear(ref.expense.dueDate);
    case 'CHANGE':
      return ref.change.bookingCode;
    case 'INVITATION':
      return ref.invitation.email;
    case 'DISCOUNT':
      return ref.discount.promoCode ?? ref.discount.name;
    default:
      return breach(
        `Nieznany rodzaj odnośnika: "${kind}". Dopisz jego etykietę do AttentionRefDto.utils.ts.`,
      );
  }
};

export interface AttentionRefPart {
  key: string;
  label: string;
  struck: boolean;
}

/** Wspólne źródło zdania tekstowego i wersji z przekreśleniem — obie liczą resztę tak samo. */
export const attentionRefParts = (
  refs: AttentionRefDto[],
): { parts: AttentionRefPart[]; rest: number } => {
  const all = refs
    .map((ref, index) => ({
      key: `${ref.kind}-${index}`,
      label: attentionRefLabel(ref),
      struck:
        ref.kind === 'BOOKING' &&
        ref.booking?.status === BookingStatus.CANCELLED,
    }))
    .filter((part) => part.label !== '');

  return {
    parts: all.slice(0, MAX_REFS),
    rest: Math.max(0, all.length - MAX_REFS),
  };
};

export const formatAttentionRefs = (refs: AttentionRefDto[]): string => {
  const { parts, rest } = attentionRefParts(refs);
  const shown = parts.map((part) => part.label).join(', ');

  return rest > 0 ? `${shown} i ${rest} więcej` : shown;
};
